import { motion, AnimatePresence } from 'framer-motion';
import HeroCry from '@/assets/logout-modal/hero-cry.webp';
import CloseButton from '@/assets/svgs/CloseButton';

interface LogoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
}

export const LogoutModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}: LogoutModalProps) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={!isLoading ? onClose : undefined}
            style={{ cursor: isLoading ? 'not-allowed' : 'default' }}
          />

          {/* Modal Container */}
          <div className="fixed left-0 right-0 top-1/2 -translate-y-1/2 z-[101] max-w-[var(--app-max-width)] mx-auto flex justify-center items-center pointer-events-none">
            <motion.div
              className="relative w-[339px] flex flex-col items-center p-3 isolate bg-[#C2CCDF] border-2 border-[rgba(46,64,86,0.2)] rounded-2xl pointer-events-auto"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            >
              {/* Title */}
              <h2 className="font-lilita text-[24px] text-white text-center [text-shadow:1px_2px_0px_#000] [-webkit-text-stroke:1px_#000]">
                Time to bounce?
              </h2>

              <motion.div
                className="absolute top-3 right-3"
                whileHover={!isLoading ? { scale: 1.05, y: -2 } : {}}
                whileTap={!isLoading ? { scale: 0.95 } : {}}
                transition={{ duration: 0.15 }}
                onClick={!isLoading ? onClose : undefined}
                style={{
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.5 : 1,
                }}
              >
                <CloseButton />
              </motion.div>
              {/* Content */}
              <div className="flex flex-col items-center gap-6 w-full max-w-[307px]">
                {/* Image Container */}
                <motion.img
                  src={HeroCry}
                  alt="Character"
                  className="w-[135px] h-[135px] object-contain z-10"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    // delay: 0.2,
                    type: 'spring',
                    stiffness: 200,
                  }}
                />

                {/* Product Info Container */}
                <div className="flex flex-col items-center -mt-24 pt-16 pb-2 w-full bg-[#8391A6] rounded-xl">
                  <p className="font-lilita text-[22px] text-white text-center [text-shadow:1px_2px_0px_#000] [-webkit-text-stroke:1px_#000] w-full">
                    See you soon. Your loot's safe.
                  </p>
                </div>

                {/* Buttons */}
                <div className="flex items-center justify-center gap-2 w-full">
                  {/* Keep Playing Button */}
                  <motion.button
                    onClick={onClose}
                    disabled={isLoading}
                    className="relative flex items-center justify-center px-3 py-2 gap-3 isolate flex-1 h-11 border-2 border-black rounded-xl overflow-hidden"
                    style={{
                      background:
                        'linear-gradient(135deg, #FF7A00 0%, #FFA500 49.04%, #FFD700 100%)',
                      opacity: isLoading ? 0.5 : 1,
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                    }}
                    whileHover={!isLoading ? { scale: 1.05, y: -2 } : {}}
                    whileTap={!isLoading ? { scale: 0.95 } : {}}
                    transition={{ duration: 0.15 }}
                  >
                    <div className="absolute inset-x-1 top-1 h-7 bg-white/30 rounded-lg z-0" />
                    <span className="font-lilita text-[24px] leading-8 text-white relative z-10 [text-shadow:1px_2px_0px_#000] [-webkit-text-stroke:1px_#000]">
                      Stay
                    </span>
                  </motion.button>

                  {/* Logout Button */}
                  <motion.button
                    onClick={onConfirm}
                    disabled={isLoading}
                    className="relative flex items-center justify-center px-3 py-2 gap-3 isolate flex-1 h-11 border-2 border-black rounded-xl overflow-hidden"
                    style={{
                      background:
                        'linear-gradient(135deg, #FF0000 0%, #D70000 49.04%, #FF6F6F 100%)',
                      opacity: isLoading ? 0.7 : 1,
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                    }}
                    whileHover={!isLoading ? { scale: 1.05, y: -2 } : {}}
                    whileTap={!isLoading ? { scale: 0.95 } : {}}
                    transition={{ duration: 0.15 }}
                  >
                    <div className="absolute inset-x-1 top-1 h-7 bg-white/30 rounded-lg z-0" />
                    <span className="font-lilita text-[24px] leading-8 text-white relative z-10 [text-shadow:1px_2px_0px_#000] [-webkit-text-stroke:1px_#000]">
                      {isLoading ? 'Logging out' : 'Logout'}
                    </span>
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};
